import React, { useEffect, useMemo,  useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/api";
import { toast } from "react-toastify";

export const ProductDetails = () => {
  const navigate = useNavigate();
  const { productId } = useParams();

  const me = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("sp_user") || "{}"); } catch { return {}; }
  }, []);
  const role = me?.role;
  const isBuyer  = role === "user" || role === "buyer";
  const isVendor = role === "vendor" || me?.isVendor === true;

  const [qty, setQty] = useState(1);

  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const addToCart = async () => {
    try {
      await api.cart.add(productId, qty);
      toast.warn("Added to cart");
    } catch (e) {
      toast.warn(e?.data?.error || e?.data?.message || "Failed to add to cart");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await api.products.getOne(productId);
        setP(data);
      } catch (e) {
        console.error(e);
        setErr(e?.data?.error || e?.data?.message || e?.message || "Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const img = p?.images?.[0] || "https://via.placeholder.com/800x220?text=No+Image";
  const price = p ? Number(p.pricePerUnit || 0).toLocaleString() : "0";

  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">
        {/* Top bar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Product Details</h1>
          <div className="w-9" />
        </div>

        {loading && <div className="px-4 mt-4 text-sm">Loading…</div>}
        {!!err && <div className="px-4 mt-4 text-sm text-[#c34040]">{err}</div>}

        {!loading && !err && p && (
          <>
            <div className="px-4 mt-4">
              <img alt="" className="w-full h-[220px] object-cover rounded-[14px]" src={img} />
            </div>

            <div className="px-4 mt-3 flex items-start justify-between">
              <div>
                <p className="font-bold">{p.name}</p>
                <p className="font-extrabold text-[20px] -mt-0.5">₦{price}</p>
                <div className="text-[12px] mt-1">
                  <span className="mr-3">Stock: {p.stockQuantity}</span>
                  <span>Visibility: {p.visibility}</span>
                </div>
                {p.category?.name && (
                  <div className="text-[12px] mt-1">Category: {p.category.name}</div>
                )}
              </div>

              {/* Right-side: vendor-only */}
              <div className="text-right">
                {isVendor && (
                  <>
                    <p className="font-extrabold">{p.ordersCount ?? 0}</p>
                    <p className="text-[12px]">Orders</p>
                  </>
                )}
              </div>
            </div>

            <div className="px-4 mt-3">
              <p className="font-extrabold">Description</p>
              <div className="mt-2 rounded-[12px] bg-white border border-[#eadfb7] p-3 text-[14px]">
                {p.description || "No description provided."}
              </div>
            </div>

            {isBuyer && (
              <div className="px-4 mt-4 flex items-center gap-3">
                <input
                  className="w-[80px] h-[40px] rounded-[10px] border border-[#5d4037] bg-white px-3 outline-none"
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
                />
                <button
                  onClick={addToCart}
                  className="h-[40px] px-4 rounded-[20px] bg-[#f7c945] font-bold text-[#5d4037] hover:opacity-90"
                >
                  Add to Cart
                </button>
              </div>
            )}

            {/* reporting visuals (placeholders) */}
            <div className="px-4 mt-4">
              <p className="font-extrabold uppercase text-[13px] tracking-wide">Sales Reporting</p>
              <div className="mt-2 flex justify-end">
                <div className="px-3 h-[28px] rounded-[8px] bg-white border border-[#eadfb7] text-[12px] inline-flex items-center gap-2">
                  Last Week
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>
                </div>
              </div>

              <div className="mt-2 bg-[#fff8e1] border border-[#eadfb7] rounded-[12px] p-4">
                <div className="h-[130px] relative">
                  <div className="absolute left-3 right-3 bottom-0 flex items-end justify-between">
                    {[60,120,80,35,150,60,75].map((h,i)=>(
                      <div key={i} className="w-7 rounded-t bg-[#5d4037]" style={{height:`${h}px`}}/>
                    ))}
                    <div className="absolute left-1/2 -translate-x-1/2 -top-3">
                      <div className="px-2 py-0.5 rounded-full bg-[#5d4037] text-[#f7c945] text-[11px] shadow">★ ₦160,000</div>
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex justify-between text-[11px] text-[#5d4037]/80">
                  {["1 Sep","2 Sep","3 Sep","4 Sep","5 Sep","6 Sep","7 Sep"].map((d)=>(<span key={d}>{d}</span>))}
                </div>
              </div>
            </div>

            <div className="px-4 mt-4">
              <p className="font-extrabold uppercase text-[13px] tracking-wide">Views Reporting</p>
              <div className="mt-2 bg-[#fff8e1] border border-[#eadfb7] rounded-[12px] p-4">
                <svg viewBox="0 0 300 120" className="w-full h-[120px]">
                  <polyline points="10,110 40,40 70,80 100,20 130,100 160,50 190,110 220,85 250,95 280,90"
                    fill="none" stroke="#5d4037" strokeWidth="4" />
                </svg>
                <div className="mt-1 flex justify-between text-[11px] text-[#5d4037]/80">
                  {["1 Sep","2 Sep","3 Sep","4 Sep","5 Sep","6 Sep","7 Sep"].map((d)=>(<span key={d}>{d}</span>))}
                </div>
              </div>
            </div>
          </>
        )}

        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
